import { Fragment, useMemo, useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

/**
 * Generic headless Combobox built on top of @headlessui/react.
 * Expects option objects with at least { id, label } shape.
 */
const ComboBox = ({
  options = [],
  value,
  onChange,
  disabled = false,
  placeholder = 'Search option',
  emptyState = 'No matches found',
  displayField = 'label',
  descriptionField,
  className = '',
  filterOptions,
  compact = false,
  disableShadow = false,
  subtleFocus = false,
  disableFocusRing = false,
  optionLimit,
}) => {
  const [query, setQuery] = useState('')

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        __label:
          typeof option === 'string'
            ? option
            : option?.[displayField] ?? option?.label ?? option?.name ?? '',
        __description:
          typeof option === 'string'
            ? ''
            : descriptionField
            ? option?.[descriptionField] ?? ''
            : option?.description ?? option?.subLabel ?? '',
      })),
    [options, displayField, descriptionField],
  )

  const filteredOptions = useMemo(() => {
    if (typeof filterOptions === 'function') {
      return filterOptions(normalizedOptions, query)
    }

    if (query.trim() === '') {
      return normalizedOptions
    }
    const loweredQuery = query.toLowerCase()
    return normalizedOptions.filter((option) =>
      option.__label.toLowerCase().includes(loweredQuery),
    )
  }, [normalizedOptions, query, filterOptions])

  const visibleOptions = useMemo(() => {
    if (!Number.isInteger(optionLimit) || optionLimit < 1) {
      return filteredOptions
    }

    return filteredOptions.slice(0, optionLimit)
  }, [filteredOptions, optionLimit])

  return (
    <Combobox value={value} onChange={onChange} disabled={disabled} by="id">
      {({ open }) => (
      <div className={`relative ${open ? 'z-[100]' : 'z-0'} ${className}`}>
        <div
            className={`group relative flex w-full items-center rounded-xl bg-glassLight text-left ${disableShadow ? '' : 'shadow-lg'} ring-1 ring-black/5 transition duration-150 ${disableFocusRing ? '' : 'focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-0'} dark:bg-glassDark dark:ring-white/10 ${disableFocusRing ? '' : subtleFocus ? 'focus-within:ring-customAccent/50 dark:focus-within:ring-customLightYellow/50' : 'focus-within:ring-customAccent dark:focus-within:ring-customLightYellow'} ${
            disabled ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          <Combobox.Input
            className={`w-full rounded-xl border-none bg-transparent text-customPrimaryText placeholder:text-customMuted focus:ring-0 dark:text-customFontMilkWhite ${
              compact ? 'py-2 pl-3 pr-9 text-xs' : 'py-2.5 pl-4 pr-10 text-sm'
            }`}
            displayValue={(option) => option?.__label ?? option?.label ?? option?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 text-customMuted transition focus:outline-none focus:text-customAccent group-focus-within:text-customAccent dark:text-customLightMilkWhite">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className={`absolute z-[9999] left-0 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-white/60 bg-white py-1 text-sm ${disableShadow ? '' : 'shadow-2xl'} focus:outline-none dark:border-white/10 dark:bg-custom_dark_primary`}>
            {filteredOptions.length === 0 ? (
            <div className={`${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} text-customSecondaryText dark:text-customLightMilkWhite/70`}>
                {emptyState}
              </div>
            ) : (
              visibleOptions.map((option) => (
                <Combobox.Option
                  key={option.id ?? option.__label}
                  className={({ active }) =>
                    `relative cursor-default select-none transition ${compact ? 'px-3 py-1.5' : 'px-4 py-2'} ${
                      active
                        ? 'bg-customAccent/10 text-customAccent dark:bg-customAccent/20'
                        : 'text-customPrimaryText dark:text-customFontMilkWhite'
                    }`
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className={`truncate ${compact ? 'text-xs' : 'text-sm'} ${selected ? 'font-semibold' : 'font-medium'}`}>
                          {option.__label}
                        </span>
                        {option.__description ? (
                          <span className="text-xs text-customSecondaryText dark:text-customLightMilkWhite/70">
                            {option.__description}
                          </span>
                        ) : null}
                      </div>
                      {selected ? (
                        <CheckIcon className="h-5 w-5 text-customAccent dark:text-customLightYellow" aria-hidden="true" />
                      ) : null}
                    </div>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
      )}
    </Combobox>
  )
}

export default ComboBox
